import {
  computeCrystallizationBonusMultiplier,
  crystallizationTierProgress,
  previewCrystallization,
} from '@sai-clicker/game-core';
import { UPGRADES_ALPHA } from '@sai-clicker/content';
import { useAnchor } from '../onboarding/useAnchor.js';
import { useGameStore } from '../stores/game.js';
import { formatNumber } from '../utils/format.js';
import styles from './CrystallizeButton.module.css';

/**
 * Three-stage visibility (JB 2026-05-25 refinement):
 *
 *   1. **Hidden** (awarenesses < EARLY_REVEAL): slot collapses to zero —
 *      нет даже намёка на кнопку. Это сохраняет чистоту первых секунд.
 *
 *   2. **Disabled-with-progress** (10 ≤ awarenesses < 100): кнопка
 *      появляется как пустая полоска с прогрессом «до первого духобалла:
 *      N ос.» Игрок видит куда идёт ещё до того как сможет нажать.
 *
 *   3. **Enabled-with-progress** (awarenesses ≥ 100): кнопка живая, можно
 *      жать. Прогресс продолжает заполняться к следующей ступени (2 шара
 *      = 400 ос., 3 шара = 900, …) — оттенок градиента слегка сдвигается
 *      по tier чтобы игрок чувствовал «приближаюсь к следующему уровню».
 *
 * Полоса прогресса лежит ПОД основным текстом и заполняется слева направо
 * через CSS-переменную `--progress: 0..1`. Цвет градиента сдвигается через
 * `--tier-hue` (0 = brown-warm для tier 1, +30 = более gold к tier 5+).
 *
 * Once shown (даже в stage 2), кнопка не возвращается в stage 1 — это
 * `hasSeenCrystallizeReveal` sticky flag в store. Без него после первой
 * кристаллизации (когда awareness → 0) кнопка бы исчезла снова.
 */
const EARLY_REVEAL_THRESHOLD = 10;

export function CrystallizeButton(): JSX.Element {
  const awarenesses = useGameStore((s) => s.state.resources.awarenesses);
  const epoch = useGameStore((s) => s.state.epoch);
  const purchasedUpgrades = useGameStore((s) => s.state.purchasedUpgrades);
  const crystallize = useGameStore((s) => s.crystallize);
  const hasSeenCrystallize = useGameStore((s) => s.hasSeenCrystallize);
  // Регистрируем кнопку как onboarding anchor — подсказка про кристаллизацию
  // прицеливается в реальный rect кнопки на любом viewport.
  const buttonAnchor = useAnchor<HTMLButtonElement>('crystallize');

  const preview = previewCrystallization(awarenesses, epoch);
  // 2026-05-26 BUG FIX: previewCrystallization не учитывает
  // crystallizationBonus от апгрейдов (u040 Алхимия кристалла,
  // u062 Тёплое возвращение и т.д.). Игрок видел «+3» в кнопке,
  // получал «+3» вместо «+4» (bonus 1.3× × 3 = 3.9 → floor 3 или 4).
  // Считаем bonus тут и показываем реальное value которое получит.
  const crystallizeBonus = computeCrystallizationBonusMultiplier(purchasedUpgrades, UPGRADES_ALPHA);
  const actualSpiritBalls = Math.floor(preview.spiritBallsToGain * crystallizeBonus);
  const ready = actualSpiritBalls > 0;
  const tierProgress = crystallizationTierProgress(awarenesses, epoch);

  // Stage 1: never reached the early-reveal threshold AND no prior
  // crystallization — completely hide.
  const everShown = hasSeenCrystallize || awarenesses >= EARLY_REVEAL_THRESHOLD;
  if (!everShown) {
    const hiddenClass = `${styles.slot ?? ''} ${styles.slotHidden ?? ''}`;
    return <div className={hiddenClass} aria-hidden />;
  }

  // Stage 2/3: button is rendered. Progress fills toward next tier.
  const buttonClass = ready
    ? (styles.button ?? '')
    : `${styles.button ?? ''} ${styles.buttonDisabled ?? ''}`;

  // Tier-hue shifts gradient warmth by spirit-ball tier. Cap at 5 so
  // late-game doesn't blast unique colors every tier.
  const tierHue = Math.min(tierProgress.nextTier, 5);
  const progressPct = (tierProgress.progress01 * 100).toFixed(1);

  // JB 2026-05-26: «плашка кристаллизации слишком перегруженная». Упрощено:
  // одна строка subtitle — либо «до 1-го: N ос.» (когда disabled) либо
  // «до +K: N ос.» (когда ready, показывает следующую ступень). Заголовок
  // + цифра — одна строка. Прогресс bar теперь явный (border + glow).
  const subtitle = ready
    ? `до +${tierProgress.nextTier.toString()}: ${formatNumber(tierProgress.awarenessesUntilNext)} ос.`
    : `до первого: ${formatNumber(tierProgress.awarenessesUntilNext)} ос.`;

  return (
    <div className={styles.slot}>
      <button
        ref={buttonAnchor}
        type="button"
        className={buttonClass}
        onClick={ready ? crystallize : undefined}
        disabled={!ready}
        aria-label={
          ready
            ? `Кристаллизовать — получить ${actualSpiritBalls.toString()} духобаллов`
            : `Кристаллизация — нужно ещё ${tierProgress.awarenessesUntilNext.toString()} осознаний`
        }
        style={{
          // Drive the progress fill width + hue shift from JS.
          ['--progress' as string]: progressPct + '%',
          ['--tier-hue' as string]: tierHue.toString(),
        }}
      >
        {/* Progress fill — absolute behind text, width via --progress var */}
        <span className={styles.progressFill} aria-hidden />
        <span className={styles.content}>
          <span className={styles.titleRow}>
            <span className={styles.glyph} aria-hidden>
              {/* Mandala progress — circular arc вокруг glyph'а заполняется
                  с progress01 (doc 28 P1). SVG отдельный layer behind the
                  ◈ символ. Дополняет horizontal bar — ring даёт «ритуальную»
                  visual progression which suits crystallization semantics. */}
              <svg className={styles.glyphMandala} viewBox="0 0 24 24" aria-hidden>
                <circle className={styles.glyphMandalaTrack} cx="12" cy="12" r="10" />
                <circle
                  className={styles.glyphMandalaArc}
                  cx="12"
                  cy="12"
                  r="10"
                  pathLength={1}
                  strokeDasharray="1 1"
                  strokeDashoffset={1 - tierProgress.progress01}
                  transform="rotate(-90 12 12)"
                />
              </svg>
              <span className={styles.glyphSymbol}>◈</span>
            </span>
            <span className={styles.label}>кристаллизовать</span>
            <span className={styles.amount}>
              {ready ? `+${formatNumber(actualSpiritBalls)}` : '·'}
            </span>
          </span>
          <span className={styles.subtitle}>{subtitle}</span>
        </span>
      </button>
    </div>
  );
}
